import React, {useState, useEffect} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Email as EmailIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import EmailSettingsUI from './EmailSettingsUI';
import SmtpConfigurationForm from './components/SmtpConfigurationForm';
import RecipientsList from './components/RecipientsList';
import SchedulerConfigurationForm from './components/SchedulerConfigurationForm';
import emailSettingsApi from '@crema/services/apis/emailSettings';
import schedulerApi from '../../../services/apis/schedulerApi';

const EmailSettingsLG = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch SMTP settings
      const settingsResponse = await emailSettingsApi.getEmailSettings();
      if (settingsResponse.data.status === 200) {
        setSmtpSettings(settingsResponse.data.data);
      } else if (settingsResponse.data.status === 404) {
        // No settings configured yet, this is normal
        setSmtpSettings(null);
      }

      // Fetch recipients
      const recipientsResponse = await emailSettingsApi.getRecipients();
      if (recipientsResponse.data.status === 200) {
        setRecipients(recipientsResponse.data.data);
      } else if (recipientsResponse.data.status === 404) {
        // No recipients configured yet, this is normal
        setRecipients([]);
      }

      // Fetch scheduler status
      try {
        const schedulerResponse = await schedulerApi.getStatus();
        if (schedulerResponse.status === 200) {
          setSchedulerStatus(schedulerResponse.data);
        }
      } catch (error) {
        console.error('Error fetching scheduler status:', error);
        setSchedulerStatus(null);
      }
    } catch (err) {
      // Only show error if it's not a 404 (which is normal for unconfigured settings)
      if (err.response?.status !== 404) {
        console.error('Error fetching email settings data:', err);
        setError('Failed to load email settings data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSuccess = (message) => {
    setSuccessMessage(message);
    fetchData(); // Refresh data
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError(null);
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant='h4' gutterBottom>
          Email Configuration
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          Configure SMTP settings and manage email recipients for daily reports
        </Typography>
      </Box>

      {error && (
        <Alert severity='error' sx={{mb: 3}} onClose={handleCloseSnackbar}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab
              icon={<SettingsIcon />}
              label='SMTP Configuration'
              iconPosition='start'
              sx={{ minWidth: { xs: 'auto', sm: '160px' } }}
            />
            <Tab
              icon={<PeopleIcon />}
              label='Recipients'
              iconPosition='start'
              sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
            />
            <Tab
              icon={<ScheduleIcon />}
              label='Scheduler'
              iconPosition='start'
              sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
            />
            <Tab 
              icon={<EmailIcon />} 
              label='Overview' 
              iconPosition='start'
              sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
            />
          </Tabs>
        </Box>

        <CardContent>
          {activeTab === 0 && (
            <SmtpConfigurationForm
              settings={smtpSettings}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}

          {activeTab === 1 && (
            <RecipientsList
              recipients={recipients}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}

          {activeTab === 2 && (
            <SchedulerConfigurationForm
              currentSettings={smtpSettings}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}

          {activeTab === 3 && (
            <EmailSettingsUI
              settings={smtpSettings}
              recipients={recipients}
              schedulerStatus={schedulerStatus}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </Box>
  );
};

export default EmailSettingsLG;
